import { useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import { ArrowLeft, Save, Truck } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/Components/ui/Button';
import { Card } from '@/Components/ui/Card';
import { Checkbox } from '@/Components/ui/Checkbox';
import { Input } from '@/Components/ui/Input';
import { Select } from '@/Components/ui/Select';
import { PageHeader } from '@/Components/data/PageHeader';
import DecimalInput from '@/Components/forms/DecimalInput';
import { Field } from '@/Components/forms/Field';
import FormStickyFooter from '@/Components/forms/FormStickyFooter';
import veiculoService, { type VeiculoInput, type VeiculoTipo } from '@/services/veiculo';

const TIPO_LABELS: Record<VeiculoTipo, string> = {
  PROPRIO: 'Próprio',
  TERCEIRO: 'Terceiro',
  AGREGADO: 'Agregado',
};

const UFS = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG',
  'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO',
];

const FORM_VAZIO: VeiculoInput = {
  placa: '',
  marca: '',
  modelo: '',
  ano: null,
  cor: '',
  renavam: '',
  chassi: '',
  rntrc: '',
  uf: '',
  tipo: 'PROPRIO',
  capacidade_kg: null,
  tara: null,
  proprietario_nome: '',
  proprietario_cpf_cnpj: '',
  is_active: true,
};

function extractErrorMessage(error: unknown): string {
  if (error instanceof AxiosError) {
    const data = error.response?.data as { message?: string; errors?: Record<string, string[]> } | undefined;
    const messages = data?.errors ? Object.values(data.errors).flat() : [];
    if (messages.length) return messages.join('\n');
    if (data?.message) return data.message;
  }
  return 'Não foi possível salvar o veículo. Verifique os dados e tente novamente.';
}

export default function VeiculoForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const editando = Boolean(id);
  const [form, setForm] = useState<VeiculoInput>(FORM_VAZIO);
  const [loading, setLoading] = useState(editando);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;

    setLoading(true);
    veiculoService.buscarPorId(id)
      .then((data) => setForm({
        placa: data.placa || '',
        marca: data.marca || '',
        modelo: data.modelo || '',
        ano: data.ano ?? null,
        cor: data.cor || '',
        renavam: data.renavam || '',
        chassi: data.chassi || '',
        rntrc: data.rntrc || '',
        uf: data.uf || '',
        tipo: data.tipo || 'PROPRIO',
        capacidade_kg: data.capacidade_kg ?? null,
        tara: data.tara ?? null,
        proprietario_nome: data.proprietario_nome || '',
        proprietario_cpf_cnpj: data.proprietario_cpf_cnpj || '',
        is_active: data.is_active,
      }))
      .catch((error) => {
        console.error('Erro ao carregar veículo', error);
        setErro('Não foi possível carregar o veículo.');
      })
      .finally(() => setLoading(false));
  }, [id]);

  const salvar = async () => {
    const placa = form.placa.trim().toUpperCase();
    if (!placa) {
      setErro('Informe a placa do veículo.');
      return;
    }

    setSalvando(true);
    setErro(null);
    try {
      const payload: VeiculoInput = {
        ...form,
        placa,
        marca: form.marca?.trim() || null,
        modelo: form.modelo?.trim() || null,
        cor: form.cor?.trim() || null,
        renavam: form.renavam?.trim() || null,
        chassi: form.chassi?.trim() || null,
        rntrc: form.rntrc?.trim() || null,
        uf: form.uf || null,
        proprietario_nome: form.proprietario_nome?.trim() || null,
        proprietario_cpf_cnpj: form.proprietario_cpf_cnpj?.trim() || null,
      };

      if (id) {
        await veiculoService.atualizar(id, payload);
      } else {
        await veiculoService.criar(payload);
      }
      navigate('/producao/veiculos');
    } catch (error) {
      console.error('Erro ao salvar veículo', error);
      setErro(extractErrorMessage(error));
    } finally {
      setSalvando(false);
    }
  };

  return (
    <form className="nx-page nx-sngpc-form-page" noValidate onSubmit={(event) => { event.preventDefault(); void salvar(); }}>
      <PageHeader
        icon={<Truck size={21} strokeWidth={2} />}
        title={editando ? 'Editar Veículo' : 'Novo Veículo'}
        description={editando ? 'Atualize os dados cadastrais do veículo.' : 'Preencha os dados para incluir um novo veículo.'}
        actions={<Button type="button" variant="ghost" onClick={() => navigate('/producao/veiculos')}><ArrowLeft size={16} /> Voltar</Button>}
      />

      <Card className="nx-page-card nx-sngpc-form-card">
        {erro ? <div className="nx-sngpc-error" role="alert" style={{ whiteSpace: 'pre-line' }}>{erro}</div> : null}

        <section className="nx-sngpc-form-section">
          <div className="nx-sngpc-form-section__header"><h2>Identificação</h2></div>
          <div className="nx-sngpc-page-form-grid">
            <Field label="Placa *" htmlFor="veiculo-placa">
              <Input id="veiculo-placa" value={form.placa} onChange={(e) => setForm({ ...form, placa: e.target.value.toUpperCase() })} disabled={loading} maxLength={10} required />
            </Field>
            <Field label="Tipo *" htmlFor="veiculo-tipo">
              <Select id="veiculo-tipo" value={form.tipo || 'PROPRIO'} onChange={(e) => setForm({ ...form, tipo: e.target.value as VeiculoTipo })} disabled={loading} required>
                {Object.entries(TIPO_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </Select>
            </Field>
            <Field label="Marca" htmlFor="veiculo-marca">
              <Input id="veiculo-marca" value={form.marca || ''} onChange={(e) => setForm({ ...form, marca: e.target.value })} disabled={loading} />
            </Field>
            <Field label="Modelo" htmlFor="veiculo-modelo">
              <Input id="veiculo-modelo" value={form.modelo || ''} onChange={(e) => setForm({ ...form, modelo: e.target.value })} disabled={loading} />
            </Field>
            <Field label="Ano" htmlFor="veiculo-ano">
              <Input id="veiculo-ano" type="number" value={form.ano ?? ''} onChange={(e) => setForm({ ...form, ano: e.target.value ? Number(e.target.value) : null })} disabled={loading} min={1900} max={2100} />
            </Field>
            <Field label="Cor" htmlFor="veiculo-cor">
              <Input id="veiculo-cor" value={form.cor || ''} onChange={(e) => setForm({ ...form, cor: e.target.value })} disabled={loading} />
            </Field>
            <Field label="UF" htmlFor="veiculo-uf">
              <Select id="veiculo-uf" value={form.uf || ''} onChange={(e) => setForm({ ...form, uf: e.target.value })} disabled={loading}>
                <option value="">Selecione</option>
                {UFS.map((uf) => <option key={uf} value={uf}>{uf}</option>)}
              </Select>
            </Field>
          </div>
        </section>

        <section className="nx-sngpc-form-section">
          <div className="nx-sngpc-form-section__header"><h2>Documentos e capacidade</h2></div>
          <div className="nx-sngpc-page-form-grid">
            <Field label="RENAVAM" htmlFor="veiculo-renavam">
              <Input id="veiculo-renavam" value={form.renavam || ''} onChange={(e) => setForm({ ...form, renavam: e.target.value })} disabled={loading} />
            </Field>
            <Field label="Chassi" htmlFor="veiculo-chassi">
              <Input id="veiculo-chassi" value={form.chassi || ''} onChange={(e) => setForm({ ...form, chassi: e.target.value.toUpperCase() })} disabled={loading} />
            </Field>
            <Field label="RNTRC" htmlFor="veiculo-rntrc">
              <Input id="veiculo-rntrc" value={form.rntrc || ''} onChange={(e) => setForm({ ...form, rntrc: e.target.value })} disabled={loading} />
            </Field>
            <Field label="Capacidade (kg)" htmlFor="veiculo-capacidade">
              <DecimalInput id="veiculo-capacidade" value={form.capacidade_kg ?? null} onValueChange={(value) => setForm({ ...form, capacidade_kg: value })} disabled={loading} />
            </Field>
            <Field label="Tara (kg)" htmlFor="veiculo-tara">
              <DecimalInput id="veiculo-tara" value={form.tara ?? null} onValueChange={(value) => setForm({ ...form, tara: value })} disabled={loading} />
            </Field>
          </div>
        </section>

        <section className="nx-sngpc-form-section">
          <div className="nx-sngpc-form-section__header"><h2>Proprietário</h2></div>
          <div className="nx-sngpc-page-form-grid">
            <Field label="Nome" htmlFor="veiculo-proprietario" className="nx-sngpc-field--full">
              <Input id="veiculo-proprietario" value={form.proprietario_nome || ''} onChange={(e) => setForm({ ...form, proprietario_nome: e.target.value })} disabled={loading} />
            </Field>
            <Field label="CPF/CNPJ" htmlFor="veiculo-cpf-cnpj">
              <Input id="veiculo-cpf-cnpj" value={form.proprietario_cpf_cnpj || ''} onChange={(e) => setForm({ ...form, proprietario_cpf_cnpj: e.target.value })} disabled={loading} />
            </Field>
            <label className="nx-sngpc-checkbox-field">
              <Checkbox checked={form.is_active ?? true} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} disabled={loading} />
              <span>Ativo</span>
            </label>
          </div>
        </section>
      </Card>

      <FormStickyFooter cancelLabel="Cancelar" onCancel={() => navigate('/producao/veiculos')} saveLabel="Salvar" loading={salvando} saveIcon={<Save size={16} />} />
    </form>
  );
}
